import { useQuery } from '@tanstack/react-query';
import { telemetryService } from '../services/telemetry/telemetryService';
import { getSummaryRulerDefault } from '../config/experiments';

export type SummaryBasis = 'unified' | 'swivel';

export interface SummaryMetrics {
  crewAverage: number;
  powerRange: { min: number; max: number };
  consistency: number; // CV%
  peakPower: number;
}

export function computeMetrics(values: number[]): SummaryMetrics {
  if (!values.length) {
    return { crewAverage: 0, powerRange: { min: 0, max: 0 }, consistency: 0, peakPower: 0 };
  }
  const n = values.length;
  const sum = values.reduce((a, b) => a + b, 0);
  const avg = sum / n;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const variance = values.reduce((a, v) => a + Math.pow(v - avg, 2), 0) / n;
  const sd = Math.sqrt(variance);
  const cv = avg > 0 ? (sd / avg) * 100 : 0;
  return {
    crewAverage: Math.round(avg),
    powerRange: { min: Math.round(min), max: Math.round(max) },
    consistency: Math.round(cv),
    peakPower: Math.round(max),
  };
}

export function alphaPlausible(alpha: number | null): boolean {
  if (alpha == null || !isFinite(alpha)) return false;
  const a = alpha as number;
  // Accept common bands: kgf+lever ~0.15-0.35 OR N+lever ~0.9-1.1
  return (a > 0.15 && a < 0.35) || (a > 0.9 && a < 1.1);
}

export function shouldUseUnified(
  r2: number | null,
  nStrokes: number,
  alpha: number | null
): boolean {
  const r2ok = typeof r2 === 'number' && r2 >= 0.88;
  const nOk = nStrokes >= 12;
  const aOk = alphaPlausible(alpha);
  return Boolean(r2ok && nOk && aOk);
}

export function useSummaryPerStrokeData(sessionId: string | null, basis?: SummaryBasis) {
  const defaultChosen =
    basis || (getSummaryRulerDefault() === 'perstroke-unified' ? 'unified' : 'swivel');
  const chosenKey = defaultChosen; // used only for query cache key

  return useQuery({
    queryKey: ['summary-perstroke', sessionId, chosenKey],
    enabled: !!sessionId,
    queryFn: async () => {
      if (!sessionId)
        return null as null | {
          basis: SummaryBasis;
          metrics: SummaryMetrics;
          series: number[];
          times?: number[];
          meta?: { alpha?: number | null; r2?: number | null; method?: 'whole' | 'drive' | null };
        };
      const chosen = defaultChosen;

      if (chosen === 'unified') {
        const unifiedWT = await telemetryService.getUnifiedPerStrokeSeriesWithTimes(sessionId);
        const calib = await telemetryService.getCalibrationInfo(sessionId);
        if (shouldUseUnified(calib?.r2 ?? null, unifiedWT.values.length, calib?.alpha ?? null)) {
          return {
            basis: 'unified' as SummaryBasis,
            metrics: computeMetrics(unifiedWT.values),
            series: unifiedWT.values,
            times: unifiedWT.times,
            meta: {
              alpha: calib?.alpha ?? null,
              r2: calib?.r2 ?? null,
              method: (calib?.method as any) ?? null,
            },
          };
        }
        // Fallback to Swivel
        const swivelWT = await telemetryService.getSwivelPerStrokeCrewSeriesWithTimes(sessionId);
        return {
          basis: 'swivel' as SummaryBasis,
          metrics: computeMetrics(swivelWT.values),
          series: swivelWT.values,
          times: swivelWT.times,
        };
      }
      // basis explicitly swivel
      const swivelWT = await telemetryService.getSwivelPerStrokeCrewSeriesWithTimes(sessionId);
      return {
        basis: 'swivel' as SummaryBasis,
        metrics: computeMetrics(swivelWT.values),
        series: swivelWT.values,
        times: swivelWT.times,
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
